import { motion } from "framer-motion";
import useContent from "@/lib/useContent";
import { TESTIMONIALS } from "@/lib/site-data";

const EASE = [0.76, 0, 0.24, 1];
const VIEWPORT = { once: true, margin: "-80px" };

export default function Testimonials() {
  const items = useContent("testimonials", TESTIMONIALS);
  if (!items.length) return null;
  const [featured, ...rest] = items;

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 md:py-40 bg-[#0A0B0A] text-[#F5F2EB]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid md:grid-cols-[220px_1fr] gap-8 md:gap-16 mb-16 md:mb-24">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#F5F2EB]/60 pt-3">
            <div className="flex items-center gap-3">
              <span className="w-6 h-px bg-[#D1492E]" />
              <span>Ch. 06 · Voices</span>
            </div>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 1, ease: EASE }}
            className="font-serif font-light text-5xl md:text-7xl leading-[0.94] tracking-tighter"
          >
            Word travels <em className="italic text-[#D1492E]">slowly,</em> like us.
          </motion.h2>
        </div>

        {/* Featured quote */}
        <motion.blockquote
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 1, ease: EASE }}
          data-testid="testimonial-featured"
          className="max-w-4xl"
        >
          <span className="font-serif italic text-[#D1492E] text-7xl md:text-8xl leading-none block -mb-6 md:-mb-8 select-none" aria-hidden>
            "
          </span>
          <p className="font-serif font-light text-3xl md:text-5xl leading-[1.15] tracking-tight">
            {featured.quote}
          </p>
          <footer className="mt-8 flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <span className="text-base font-medium">{featured.name}</span>
            <span className="text-sm text-[#F5F2EB]/55">{featured.origin}</span>
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#D1492E] border border-[#D1492E]/40 px-3 py-1.5">
              {featured.journey}
            </span>
          </footer>
        </motion.blockquote>

        {/* Secondary quotes */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-2 gap-10 md:gap-16 border-t border-[#F5F2EB]/15 pt-14 md:pt-16">
          {rest.map((t, i) => (
            <motion.blockquote
              key={t.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={VIEWPORT}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              data-testid={`testimonial-${i}`}
              className={i % 2 === 1 ? "md:mt-14" : ""}
            >
              <p className="font-serif font-light text-xl md:text-2xl leading-[1.4] tracking-tight text-[#F5F2EB]/85">
                "{t.quote}"
              </p>
              <footer className="mt-6 flex flex-wrap items-baseline gap-x-5 gap-y-2">
                <span className="text-sm font-medium">{t.name}</span>
                <span className="text-xs text-[#F5F2EB]/55">{t.origin}</span>
                <span className="text-[9px] uppercase tracking-[0.24em] text-[#F5F2EB]/60">
                  {t.journey}
                </span>
              </footer>
            </motion.blockquote>
          ))}
        </div>
      </div>
    </section>
  );
}
